import { computed, ref } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { findTutorial } from './tutorials';
import type { Tutorial } from './types';

// Module-scope singletons — the tutorial overlay is global. Multiple components
// can call useTutorial() and they all share this state.
const activeTutorial = ref<Tutorial | null>(null);
const currentStepIndex = ref(0);

const seenKey = (sub: string | null) => `catl_tutorial_seen:${sub ?? '_anon'}`;

function readSeen(sub: string | null): Set<string> {
    try {
        const raw = JSON.parse(localStorage.getItem(seenKey(sub)) || 'null');
        return new Set(Array.isArray(raw) ? raw : []);
    } catch {
        return new Set();
    }
}

function writeSeen(sub: string | null, seen: Set<string>) {
    if (seen.size === 0) localStorage.removeItem(seenKey(sub));
    else localStorage.setItem(seenKey(sub), JSON.stringify([...seen]));
}

export function useTutorial() {
    const { sub } = useAuth();

    const hasSeen = (id: string): boolean => readSeen(sub.value).has(id);

    const markSeen = (id: string) => {
        const seen = readSeen(sub.value);
        seen.add(id);
        writeSeen(sub.value, seen);
    };

    const startTutorial = (id: string) => {
        const t = findTutorial(id);
        if (!t) {
            console.warn(`[useTutorial] No tutorial registered with id '${id}'.`);
            return;
        }
        if (t.steps.length === 0) return;
        activeTutorial.value = t;
        currentStepIndex.value = 0;
    };

    const maybeStartTutorial = (id: string) => {
        if (hasSeen(id)) return;
        startTutorial(id);
    };

    const advanceStep = () => {
        if (!activeTutorial.value) return;
        const next = currentStepIndex.value + 1;
        if (next >= activeTutorial.value.steps.length) {
            markSeen(activeTutorial.value.id);
            activeTutorial.value = null;
            currentStepIndex.value = 0;
        } else {
            currentStepIndex.value = next;
        }
    };

    const goBack = () => {
        if (!activeTutorial.value) return;
        if (currentStepIndex.value > 0) currentStepIndex.value -= 1;
    };

    const canGoBack = computed(() => currentStepIndex.value > 0);
    const canGoNext = computed(() => {
        const t = activeTutorial.value;
        return t !== null && currentStepIndex.value < t.steps.length - 1;
    });

    // Operator dismissed the tour explicitly. Treat as seen so it doesn't pop
    // up again on the next mount — replaying is a deliberate action via a
    // Restart button.
    const skipCurrent = () => {
        if (!activeTutorial.value) return;
        markSeen(activeTutorial.value.id);
        activeTutorial.value = null;
        currentStepIndex.value = 0;
    };

    // Stop without marking — used internally when the target element vanishes
    // and we want to let the user re-enter the tour later.
    const stopTutorial = () => {
        activeTutorial.value = null;
        currentStepIndex.value = 0;
    };

    const currentStep = computed(() => {
        const t = activeTutorial.value;
        if (!t) return null;
        return t.steps[currentStepIndex.value] ?? null;
    });

    const isActive = computed(() => activeTutorial.value !== null);

    return {
        activeTutorial: computed(() => activeTutorial.value),
        currentStep,
        currentStepIndex: computed(() => currentStepIndex.value),
        isActive,
        canGoBack,
        canGoNext,
        startTutorial,
        maybeStartTutorial,
        advanceStep,
        goBack,
        skipCurrent,
        stopTutorial,
        hasSeen,
    };
}
